'use client';

import { ErrorSource } from '@/constants/apiFields';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ERROR_MANAGER_MESSAGES } from './messages';

/**
 * ErrorManagerContextProvider manages error handling and display across multiple data fetching operations.
 *
 * It provides:
 * - Centralized error state management
 * - Unified error display through a single alert
 * - Coordinated retry functionality
 * - Deduplication of error handling
 *
 * Key behaviors:
 * - Stores errors with unique IDs and retry functions
 * - Groups related errors by component
 * - Shows a single alert for all active errors
 * - Provides retry functionality that:
 *   - Deduplicates retry calls for the same data source
 *   - Clears errors after successful retries
 *   - Handles both individual and batch retries
 */

// Types
export interface ErrorState {
  id: string;
  source: ErrorSource;
  error?: {
    message: string;
    status?: number;
  };
  retryFn: () => void;
  componentId: string;
}

type AlertMessage = {
  title: string;
  description: JSX.Element | string;
  variant: 'info' | 'warning' | 'danger' | 'success' | 'opportunity';
  onActionClick: () => void;
};

interface ErrorManagerContextType {
  /**
   * Registers a new error in the error manager state
   * @param error The error state to register
   */
  registerError: (error: ErrorState) => void;
  /**
   * Clears a specific error by its ID
   * @param id The ID of the error to clear
   */
  clearError: (id: string) => void;
  /**
   * Clears all errors for specified component IDs
   * @param componentIds Array of component IDs to clear errors for
   */
  clearAllErrors: (...componentIds: string[]) => void;
  /**
   * Clears all errors where component ID contains any of the specified strings
   * @param componentIdContaining Array of strings to match against component IDs
   */
  dangerouslyClearAllErrors: (...componentIdContaining: string[]) => void;
  /**
   * Gets all errors
   * @returns Array of all errors
   */
  getAllErrors: () => ErrorState[];
  /**
   * Gets all errors associated with a specific component
   * @param componentId The ID of the component to get errors for
   * @returns Array of errors for the specified component
   */
  getErrorsForComponent: (componentId: string) => ErrorState[];
  /**
   * Gets all errors associated with a specific component
   * @param componentId The ID of the component to get errors for
   * @returns Array of errors for the specified component
   */
  /**
   * Checks if there are any errors for a specific component
   * @param componentId The ID of the component to check for errors
   * @returns True if there are errors for the component, false otherwise
   */
  hasErrorsForComponent: (componentId: string) => boolean;
  /**
   * Retries all failed operations and clears their associated errors
   * Groups retry operations by component and source to avoid duplicate retries
   */
  retryAllErrors: () => void;
  /**
   * The current alert message to display
   */
  alertMessage: AlertMessage | null;
  /**
   * Sets the current alert message to display
   */
  setAlertMessage: React.Dispatch<React.SetStateAction<AlertMessage | null>>;
  /**
   * Closes the current alert message
   */
  closeAlert: () => void;
}

const ErrorManagerContext = createContext<ErrorManagerContextType | undefined>(undefined);

export const useErrorManager = () => {
  const context = useContext(ErrorManagerContext);

  if (!context) {
    throw new Error('useErrorManager must be used within an ErrorManagerContextProvider');
  }

  return context;
};

export const ErrorManagerContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorState[]>([]);
  const [alertMessage, setAlertMessage] = useState<AlertMessage | null>(null);

  const { formatMessage } = useIntl();

  const closeAlert = useCallback(() => {
    setAlertMessage(null);
  }, []);

  /**
   * Registers a new error in the error manager state
   * @param error The error state to register
   */
  const registerError = useCallback((error: ErrorState) => {
    setErrors((prev) => {
      // Check if error already exists with same content
      const existingError = prev.find((e) => e.id === error.id);
      if (
        existingError &&
        existingError.source === error.source &&
        existingError.componentId === error.componentId &&
        existingError.error?.message === error.error?.message &&
        existingError.error?.status === error.error?.status
      ) {
        // Error already exists with same content, no need to update
        return prev;
      }

      // Remove existing error and add new one
      const filtered = prev.filter((e) => e.id !== error.id);
      return [...filtered, error];
    });
  }, []);

  /**
   * Clears a specific error by its ID
   * @param id The ID of the error to clear
   */
  const clearError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  /**
   * Clears all errors for specified component IDs
   * @param componentIds Array of component IDs to clear errors for
   */
  const clearAllErrors = useCallback((...componentIds: string[]) => {
    setErrors((prev) => prev.filter((e) => !componentIds.includes(e.componentId)));
  }, []);

  /**
   * Clears all errors where component ID contains any of the specified strings
   * @param componentIdContaining Array of strings to match against component IDs
   */
  const dangerouslyClearAllErrors = useCallback((...componentIdContaining: string[]) => {
    setErrors((prev) =>
      prev.filter((e) => !componentIdContaining.some((id) => e.componentId.includes(id))),
    );
  }, []);

  /**
   * Gets all errors
   * @returns Array of all errors
   */
  const getAllErrors = useCallback(() => {
    return errors;
  }, [errors]);

  /**
   * Gets all errors associated with a specific component
   * @param componentId The ID of the component to get errors for
   * @returns Array of errors for the specified component
   */
  const getErrorsForComponent = useCallback(
    (componentId: string) => {
      return errors.filter((e) => e.componentId === componentId);
    },
    [errors],
  );

  /**
   * Checks if there are any errors for a specific component
   * @param componentId The ID of the component to check for errors
   * @returns True if there are errors for the component, false otherwise
   */
  const hasErrorsForComponent = useCallback(
    (componentId: string) => {
      return errors.some((e) => e.componentId === componentId);
    },
    [errors],
  );

  /**
   * Retries all failed operations and clears their associated errors
   * Groups retry operations by component and source to avoid duplicate retries
   */
  const retryAllErrors = useCallback(() => {
    const errorsByComponent = errors.reduce(
      (acc, error) => {
        if (!acc[error.componentId]) acc[error.componentId] = [];
        acc[error.componentId].push(error);
        return acc;
      },
      {} as Record<string, ErrorState[]>,
    );

    const componentsWithUniqueSources = new Map<string, Map<string, Function>>();

    Object.entries(errorsByComponent).forEach(([componentId, componentErrors]) => {
      const uniqueSources = new Map<string, Function>();

      componentErrors.forEach((error) => {
        if (!uniqueSources.has(error.source)) {
          uniqueSources.set(error.source, error.retryFn);
        }
      });

      componentsWithUniqueSources.set(componentId, uniqueSources);
    });

    componentsWithUniqueSources.forEach((uniqueSources, componentId) => {
      uniqueSources.forEach((retryFn) => {
        retryFn();
      });

      clearAllErrors(componentId);
    });
  }, [errors, clearAllErrors]);

  /**
   * Constructs an error message based on which data sources failed
   * @returns A user-friendly error message string
   */
  const constructErrorMessage = useCallback((): string => {
    const uniqueSources = Array.from(new Set(errors.map((error) => error.source)));

    const failedSessionData = uniqueSources.some((source) => source === 'fetchSessionDetails');
    const failedAggregatedData = uniqueSources.some(
      (source) => source === 'fetchAggregatedDetails',
    );
    const failedDatesData = uniqueSources.some((source) => source === 'fetchSessionDates');

    // Count how many categories failed
    const failedCategories = [failedSessionData, failedAggregatedData, failedDatesData].filter(
      Boolean,
    ).length;

    if (failedCategories === 3) {
      return formatMessage(ERROR_MANAGER_MESSAGES.somethingWentWrong);
    }

    if (failedCategories === 2) {
      return formatMessage(ERROR_MANAGER_MESSAGES.someSectionsMayNotDisplayCorrectly);
    }

    // Single category failures - be more specific
    if (failedSessionData) {
      return formatMessage(ERROR_MANAGER_MESSAGES.someSessionInformationMayNotDisplayCorrectly);
    }

    if (failedAggregatedData) {
      return formatMessage(
        ERROR_MANAGER_MESSAGES.someAggregatedSalesInformationMayNotDisplayCorrectly,
      );
    }

    if (failedDatesData) {
      return formatMessage(ERROR_MANAGER_MESSAGES.eventDatesAreCurrentlyUnavailable);
    }

    // Fallback for unknown sources
    return formatMessage(ERROR_MANAGER_MESSAGES.somethingWentWrong);
  }, [errors, formatMessage]);

  // Update alert message when errors change
  // Note: We intentionally exclude constructErrorMessage and retryAllErrors from deps
  // to prevent infinite loops. Both functions are recreated when errors change, which
  // would cause this effect to run again, leading to infinite recursion. Since the
  // effect already runs when errors change, we always have the current function versions.
  useEffect(() => {
    if (errors.length > 0) {
      setAlertMessage({
        title: formatMessage(ERROR_MANAGER_MESSAGES.troubleFetchingData),
        description: constructErrorMessage(),
        variant: 'warning',
        onActionClick: () => {
          retryAllErrors();
          setAlertMessage(null);
        },
      });
    } else {
      // Clear alert if no errors
      setAlertMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, formatMessage]);

  const contextValue: ErrorManagerContextType = useMemo(
    () => ({
      registerError,
      clearError,
      clearAllErrors,
      dangerouslyClearAllErrors,
      getAllErrors,
      getErrorsForComponent,
      hasErrorsForComponent,
      retryAllErrors,
      alertMessage,
      setAlertMessage,
      closeAlert,
    }),
    [
      registerError,
      clearError,
      clearAllErrors,
      dangerouslyClearAllErrors,
      getAllErrors,
      getErrorsForComponent,
      hasErrorsForComponent,
      retryAllErrors,
      alertMessage,
      setAlertMessage,
      closeAlert,
    ],
  );

  return (
    <ErrorManagerContext.Provider value={contextValue}>{children}</ErrorManagerContext.Provider>
  );
};
